/** Plugin environments — the saved override set and the jobs that apply it. */
import { apiGet, apiPost } from '../client.js';
import type {
  ApplyEnvironmentBody,
  EnvironmentJobOut,
  PluginEnvironmentOut,
} from '../types.js';

function base(name: string): string {
  return `/api/v1/admin/plugins/${encodeURIComponent(name)}/environment`;
}

/** The saved set, declared requirements, environment on disk, and active job. */
export function getPluginEnvironment(name: string): Promise<PluginEnvironmentOut> {
  return apiGet<PluginEnvironmentOut>(base(name));
}

/**
 * Queue a rebuild with the whole submitted set. The set becomes the saved
 * set only when the job succeeds. A `409` while another job is live carries
 * `active: {id, kind, state}` in the error details; a `422` carries
 * `errors: string[]`, one per failed entry.
 */
export function applyPluginEnvironment(
  name: string,
  body: ApplyEnvironmentBody,
): Promise<EnvironmentJobOut> {
  return apiPost<EnvironmentJobOut>(`${base(name)}/apply`, body);
}

/** The plugin's environment jobs, newest first. */
export function listEnvironmentJobs(
  name: string,
  limit = 20,
): Promise<EnvironmentJobOut[]> {
  return apiGet<EnvironmentJobOut[]>(`${base(name)}/jobs`, { limit });
}

/** Every pending or running environment job; the marketplace polls this. */
export function listActiveEnvironmentJobs(): Promise<EnvironmentJobOut[]> {
  return apiGet<EnvironmentJobOut[]>('/api/v1/admin/environment-jobs/active');
}

export function getEnvironmentJob(id: string): Promise<EnvironmentJobOut> {
  return apiGet<EnvironmentJobOut>(
    `/api/v1/admin/environment-jobs/${encodeURIComponent(id)}`,
  );
}
